import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";

// News feed lives at: https://news.verde2050.com/ (JSON at raw.githubusercontent).
// We pull the same editions.json the news site uses, so the home page always
// shows the same three most-recent editions without a separate CMS.
export const EDITIONS_URL = "https://raw.githubusercontent.com/verde2050/land-intelligence/main/editions.json";
export const EDITION_URL = (date) => `https://news.verde2050.com/edition.html?date=${encodeURIComponent(date)}`;

export function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function GetThePlotFeed() {
    const [editions, setEditions] = useState(null); // null = loading, [] = failed/empty
    const [error, setError] = useState(false);

    useEffect(() => {
        let alive = true;
        fetch(EDITIONS_URL, { cache: "no-store" })
            .then((r) => {
                if (!r.ok) throw new Error("feed http " + r.status);
                return r.json();
            })
            .then((d) => {
                if (!alive) return;
                const list = Array.isArray(d) ? d : (d.editions || []);
                setEditions(list.slice(0, 3));
            })
            .catch(() => {
                if (!alive) return;
                setError(true);
                setEditions([]);
            });
        return () => { alive = false; };
    }, []);

    if (editions === null) {
        return (
            <div data-testid="plot-feed-loading" className="grid md:grid-cols-3 gap-px bg-hairline border border-hairline">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="bg-cream p-6 md:p-7 min-h-[180px] animate-pulse">
                        <div className="h-3 w-16 bg-hairline mb-4" />
                        <div className="h-4 w-3/4 bg-hairline mb-2" />
                        <div className="h-4 w-1/2 bg-hairline" />
                    </div>
                ))}
            </div>
        );
    }

    if (!editions.length) {
        return (
            <a
                href="https://news.verde2050.com"
                target="_blank"
                rel="noreferrer"
                data-testid="plot-feed-fallback"
                className="block bg-cream border border-hairline p-8 md:p-10 hover:border-forest transition-colors group"
            >
                <p className="mono-label text-terracotta mb-4">Latest editions</p>
                <p className="font-serif text-2xl md:text-3xl text-forest leading-tight mb-2">
                    Read the daily briefing on news.verde2050.com
                </p>
                <span className="inline-flex items-center gap-2 mono-label text-forest group-hover:text-terracotta transition-colors">
                    Open Get The Plot
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </span>
            </a>
        );
    }

    return (
        <div data-testid="plot-feed" className="grid md:grid-cols-3 gap-px bg-hairline border border-hairline rounded-2xl overflow-hidden">
            {editions.map((e) => (
                <a
                    key={e.date}
                    href={EDITION_URL(e.date)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`plot-card-${e.date}`}
                    className="group bg-cream p-6 md:p-7 flex flex-col justify-between min-h-[220px] hover:bg-forest hover:text-cream transition-colors"
                >
                    <div>
                        <p className="mono-label text-terracotta group-hover:text-terracotta mb-4">{formatDate(e.date)}</p>
                        <p className="font-serif text-lg md:text-xl leading-snug text-forest group-hover:text-cream mb-3 line-clamp-3 transition-colors">
                            {e.title}
                        </p>
                        {e.hook && (
                            <p className="text-xs text-muted_text group-hover:text-cream/70 leading-relaxed line-clamp-2 transition-colors">
                                {e.hook}
                            </p>
                        )}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-2 mono-label text-forest group-hover:text-terracotta transition-colors">
                        Read
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                    </span>
                </a>
            ))}
        </div>
    );
}
